import { useStore } from '@tanstack/react-form';
import { Alert, Button, Card } from '@ui/themed';
import { t } from 'i18next';
import { useRef, useState } from 'react';
import { SettingsSection } from '../../../components/settings-section';
import { isShopify, type Platform, useEnv } from '../../../context/env-context';
import { useHandlers } from '../../../context/page-context';
import { useSettings } from '../../../context/settings-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { useAppForm } from '../../../lib/app-form';
import { CREDENTIAL_FIELDS, type DisplayPlatform, STORE_ID_FIELDS } from '../constants';
import { useConnectPlatform } from '../hooks/use-connect-platform';
import { buildPlatformPayload, isPlatformValidationError, platformSchema } from '../schemas';
import type { PlatformFormValues } from '../types';
import { CredentialField } from './credential-field';
import { PlatformOptionList } from './platform-option-list';
import { ShopifyOrderActionsCard } from './shopify-order-actions-card';
import { StoreIdField } from './store-id-field';

const PAGE = 'features:platform.pages.platform_page' satisfies I18nPrefix;

export function PlatformForm() {
  const env = useEnv();
  const settings = useSettings();
  const { navigationHandler } = useHandlers();
  const connect = useConnectPlatform();

  const platformMessageRef = useRef<HTMLDivElement>(null);
  const [success, setSuccess] = useState<string | null>(
    env.onboarding_platform ? t(`${PAGE}.form.success_connected`) : null,
  );
  const [bannerMessages, setBannerMessages] = useState<string[]>([]);

  const connectedPlatform = env.platform;
  const normalisedPlatform: DisplayPlatform | '' = !connectedPlatform
    ? ''
    : connectedPlatform === 'shopify_plus'
      ? 'shopify'
      : connectedPlatform;

  let platformStoreId = env.store_id ?? '';
  if (isShopify(env.connected_platform)) {
    platformStoreId = platformStoreId.replace('.myshopify.com', '');
  }
  const basicAuth = settings.igniteBasicAuth;

  const defaultValues: PlatformFormValues = {
    platform: normalisedPlatform,
    platform_store_id: platformStoreId,
    woocommerce_api_key: '',
    woocommerce_api_secret: '',
    woocommerce_basic_user: basicAuth?.username ?? '',
    woocommerce_basic_pass: basicAuth?.password ?? '',
    magento_consumer_key: '',
    magento_consumer_secret: '',
    magento_access_token: '',
    magento_access_token_secret: '',
  };

  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: platformSchema },
    onSubmit: ({ value }) => {
      setBannerMessages([]);
      if (!value.platform) {
        setBannerMessages([t(`${PAGE}.form.select_platform`)]);
        return;
      }
      const payload = buildPlatformPayload(platformSchema.parse(value));
      connect.mutate(payload, {
        onSuccess: (data) => {
          setSuccess(data.message ?? t(`${PAGE}.form.success_connected`));
          platformMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        onError: (err) => {
          if (isPlatformValidationError(err)) {
            const { data } = err;
            const messages = [...(data.message ? [data.message] : [])];
            for (const fieldMessages of Object.values(data.errors)) {
              messages.push(...fieldMessages);
            }
            // remove duplicates as data.message and data.errors can contain the same message
            const uniqueMessages = Array.from(new Set(messages));
            setBannerMessages(uniqueMessages);
          } else {
            setBannerMessages([t(`${PAGE}.form.generic_error`)]);
          }
        },
      });
    },
  });

  const selectedPlatform = useStore(
    form.store,
    (state) => state.values.platform as DisplayPlatform | '',
  );
  const storeIdConfig =
    !selectedPlatform || selectedPlatform === 'custom'
      ? undefined
      : STORE_ID_FIELDS[selectedPlatform];
  const storeIdDisabled = !!env.store_id;
  const hasStoredValue = !!env.store_id;

  const isShopifySelected = selectedPlatform === 'shopify' && isShopify(env.connected_platform);
  const showOrderActions = isShopifySelected;

  const woocommerceApiConfigs = CREDENTIAL_FIELDS.woocommerce?.slice(0, 2) ?? [];
  const woocommerceBasicAuthConfigs = CREDENTIAL_FIELDS.woocommerce?.slice(2) ?? [];
  const magentoConfigs = CREDENTIAL_FIELDS.magento ?? [];

  const completedInstall = !!navigationHandler.query().completedInstall;

  function renderCredentials(configs: (typeof magentoConfigs)[number][]) {
    return configs.map((config) => (
      <form.Field key={config.key} name={config.key}>
        {(field) => (
          <CredentialField
            config={config}
            value={field.state.value ?? ''}
            onChange={field.handleChange}
            hasStoredValue={hasStoredValue}
            error={field.state.meta.errors[0]?.message}
          />
        )}
      </form.Field>
    ));
  }

  return (
    <>
      <div ref={platformMessageRef} className="space-y-4">
        {completedInstall && (
          <Alert
            variant="success"
            headingLevel="h2"
            title={t(`${PAGE}.form.completed_install_title`)}
          >
            <span className="text-green-800">
              <button
                type="button"
                className="font-medium text-green-800"
                onClick={() => navigationHandler.navigate('/configuration/onboarding')}
              >
                {t(`${PAGE}.form.completed_install_cta`)}
              </button>{' '}
              {t(`${PAGE}.form.completed_install_tail`)}
            </span>
          </Alert>
        )}
        {success && (
          <Alert variant="success" headingLevel="h2">
            {success}
          </Alert>
        )}
        {bannerMessages.length > 0 && (
          <Alert variant="warning" headingLevel="h2">
            <div className="flex flex-col gap-1">
              {bannerMessages.map((message) => (
                <p key={message} className="text-sm">
                  {message}
                </p>
              ))}
            </div>
          </Alert>
        )}
      </div>

      <Card>
        <Card.Body>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <fieldset>
              <PlatformOptionList
                value={selectedPlatform}
                disabledFor={(key) => !!env.store_id && key !== selectedPlatform}
                onSelect={(key) => form.setFieldValue('platform', key as DisplayPlatform)}
              />
            </fieldset>

            {storeIdConfig && (
              <form.Field name="platform_store_id">
                {(field) => (
                  <StoreIdField
                    platform={selectedPlatform as Platform}
                    config={storeIdConfig}
                    value={field.state.value}
                    onChange={(value) => {
                      const normalised =
                        selectedPlatform === 'bigcommerce' ? value.replace(/^store-/, '') : value;
                      field.handleChange(normalised);
                    }}
                    disabled={storeIdDisabled}
                    error={field.state.meta.errors[0]?.message}
                  />
                )}
              </form.Field>
            )}

            {selectedPlatform === 'woocommerce' && (
              <>
                <SettingsSection
                  title={t(`${PAGE}.form.woocommerce_api_config.title`)}
                  description={t(`${PAGE}.form.woocommerce_api_config.description`)}
                >
                  {renderCredentials(woocommerceApiConfigs)}
                </SettingsSection>
                <SettingsSection
                  title={t(`${PAGE}.form.basic_auth.title`)}
                  description={t(`${PAGE}.form.basic_auth.description`)}
                >
                  {renderCredentials(woocommerceBasicAuthConfigs)}
                </SettingsSection>
              </>
            )}

            {selectedPlatform === 'magento' && (
              <SettingsSection
                title={t(`${PAGE}.form.magento_api_config.title`)}
                description={t(`${PAGE}.form.magento_api_config.description`)}
              >
                {renderCredentials(magentoConfigs)}
              </SettingsSection>
            )}

            <div>
              <Button type="submit" loading={connect.isPending}>
                {t(`${PAGE}.form.save`)}
              </Button>
            </div>
          </form>
        </Card.Body>
      </Card>

      {showOrderActions && <ShopifyOrderActionsCard />}
    </>
  );
}
